import { useState } from 'react';
import { Badge, Button, Card, EmptyState, fieldClass, LoadingList, PageHeader, SectionHeader, Table, Td, Th } from '@timely/ui';
import { holds } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useTeam } from '../team/useTeam';
import {
    MappingError, applyReconciliation, previewReconciliation, removeOwnerMapping, setOwnerMapping,
    useOwnerCodesSummary, type ApplyResult, type ReconciliationCandidate,
} from '../reconciliation/useReconciliation';

/**
 * Ownership reconciliation.
 *
 * An Admin/Manager setup screen, deliberately away from the daily collector
 * workflow — configuring what an imported code means is `team.manage`;
 * previewing and applying what that means for the book is
 * `customers.assign`. Neither step moves an account by itself: a mapping
 * saved here changes nothing until Apply is pressed, on exactly the accounts
 * the preview showed, and never on one that has changed since.
 */
export function Reconciliation() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const canMap = holds(context, 'team.manage');
    const canReconcile = holds(context, 'customers.assign');

    const { codes, loading, error, reload } = useOwnerCodesSummary(workspace.id);
    const { members } = useTeam(context);
    const activeMembers = members.filter(m => m.status === 'active');

    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busyCode, setBusyCode] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<ReconciliationCandidate[] | null>(null);
    const [checked, setChecked] = useState<Set<string>>(new Set());
    const [previewing, setPreviewing] = useState(false);
    const [applying, setApplying] = useState(false);
    const [results, setResults] = useState<ApplyResult[] | null>(null);

    if (!canMap && !canReconcile) {
        return (
            <Page>
                <PageHeader title="Account owners" />
                <EmptyState title="Nothing to show you here" hint="Ask an admin about account owners." className="!px-0" />
            </Page>
        );
    }

    const onMap = async (code: string, memberId: string) => {
        setBusyCode(code);
        setNotice(null);
        try {
            await setOwnerMapping(workspace.id, code, memberId);
            await reload();
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof MappingError ? err.message : 'That did not work.' });
        } finally {
            setBusyCode(null);
        }
    };

    const onUnmap = async (code: string) => {
        setBusyCode(code);
        setNotice(null);
        try {
            await removeOwnerMapping(workspace.id, code);
            await reload();
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof MappingError ? err.message : 'That did not work.' });
        } finally {
            setBusyCode(null);
        }
    };

    const runPreview = async () => {
        setPreviewing(true);
        setNotice(null);
        setResults(null);
        try {
            const rows = await previewReconciliation(workspace.id);
            setCandidates(rows);
            setChecked(new Set(rows.map(r => r.customerId)));
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof MappingError ? err.message : 'That did not work.' });
        } finally {
            setPreviewing(false);
        }
    };

    const runApply = async () => {
        if (!candidates) return;
        const chosen = candidates.filter(c => checked.has(c.customerId));
        if (chosen.length === 0) return;
        setApplying(true);
        setNotice(null);
        try {
            const outcome = await applyReconciliation(workspace.id, chosen);
            setResults(outcome);
            setCandidates(null);
            await reload();
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof MappingError ? err.message : 'That did not work.' });
        } finally {
            setApplying(false);
        }
    };

    const counted = (status: ApplyResult['status']) => (results ?? []).filter(r => r.status === status).length;

    return (
        <Page>
            <PageHeader
                title="Account owners"
                subtitle="The owner codes an imported sheet carries: what each means, and — separately — applying it to the accounts that carry it."
            />

            {notice && <div className="mb-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {error && <div className="mb-4"><FormNotice tone="error">{error}</FormNotice></div>}

            <Card className="overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading owner codes" rows={4} />
                ) : codes.length === 0 ? (
                    <EmptyState
                        title="No imported owner codes yet"
                        hint="A synced sheet’s owner codes appear here to map. TallyPrime carries none: give each account its collector in Customers, or on the account."
                    />
                ) : (
                    // Scrolls sideways inside the card on a phone rather than
                    // cutting off the mapping controls at the right.
                    <Table caption="Owner codes from the sheet, how many accounts carry each, and who each code means.">
                        <thead>
                            <tr>
                                <Th className="w-40">Code in the sheet</Th>
                                <Th numeric className="w-28">Accounts</Th>
                                <Th>Means</Th>
                                {canMap && <Th className="text-right"><span className="sr-only">Change</span></Th>}
                            </tr>
                        </thead>
                        <tbody>
                            {codes.map(c => (
                                <tr key={c.importedCode}>
                                    <Td className="num font-semibold text-label">{c.importedCode}</Td>
                                    <Td numeric>{c.customerCount}</Td>
                                    <Td>
                                        {c.mappedMemberId ? (
                                            <>
                                                {c.mappedMemberName}
                                                {c.mappedMemberStatus !== 'active' && <Badge tone="warn" className="ml-2">not active</Badge>}
                                            </>
                                        ) : c.suggestedMemberId ? (
                                            <span className="text-label-3">
                                                Suggested: {c.suggestedMemberName}
                                            </span>
                                        ) : (
                                            <span className="text-label-3">Not mapped</span>
                                        )}
                                    </Td>
                                    {canMap && (
                                        <Td className="text-right">
                                            <div className="inline-flex items-center gap-1.5">
                                                <select
                                                    aria-label={`Who ${c.importedCode} is`}
                                                    disabled={busyCode === c.importedCode}
                                                    value={c.mappedMemberId ?? ''}
                                                    onChange={e => { if (e.target.value) void onMap(c.importedCode, e.target.value); }}
                                                    className={fieldClass()}
                                                >
                                                    <option value="">
                                                        {c.suggestedMemberId ? `Use suggestion (${c.suggestedMemberName})` : 'Choose a member…'}
                                                    </option>
                                                    {activeMembers.map(m => (
                                                        <option key={m.memberId} value={m.memberId}>{m.displayName}</option>
                                                    ))}
                                                </select>
                                                {c.suggestedMemberId && !c.mappedMemberId && (
                                                    <Button variant="row" size="sm" disabled={busyCode === c.importedCode}
                                                        onClick={() => void onMap(c.importedCode, c.suggestedMemberId!)}>
                                                        Use
                                                    </Button>
                                                )}
                                                {c.mappedMemberId && (
                                                    <Button variant="row" size="sm" disabled={busyCode === c.importedCode}
                                                        onClick={() => void onUnmap(c.importedCode)}>
                                                        Unmap
                                                    </Button>
                                                )}
                                            </div>
                                        </Td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card>

            {/* Matching applies codes to accounts; with no codes there is nothing to match, so the step waits until a
                sheet brings some rather than offering a Preview that can only come back empty. */}
            {canReconcile && (codes.length > 0 || candidates !== null || results !== null) && (
                <>
                    <SectionHeader
                        title="Match the accounts"
                        subtitle="Nothing moves until you review the accounts below and press Apply."
                        className="mt-8 mb-3"
                        actions={
                            <Button variant="secondary" disabled={previewing} onClick={() => { void runPreview(); }} busy={previewing}>
                                {previewing ? 'Checking…' : 'Preview'}
                            </Button>
                        }
                    />

                    {results && (
                        <Card className="mb-3 p-5">
                            <p className="text-body font-semibold text-label mb-2">Applied</p>
                            <div className="flex flex-wrap gap-x-5 gap-y-1 text-secondary">
                                <Badge variant="text" dot tone="pos">{counted('applied')} applied</Badge>
                                <Badge variant="text" dot tone="warn">{counted('conflicted')} conflicted — changed since preview</Badge>
                                <Badge variant="text" dot tone="neutral">{counted('skipped_unmapped')} skipped — no active mapping</Badge>
                                <Badge variant="text" dot tone="neutral">{counted('unchanged')} already correct</Badge>
                            </div>
                        </Card>
                    )}

                    {candidates && (
                        <Card className="overflow-hidden">
                            {candidates.length === 0 ? (
                                <EmptyState title="Nothing to change" hint="Every account already has the owner its code maps to." />
                            ) : (
                                <>
                                    <Table caption="Accounts whose owner would change, and to whom.">
                                        <thead>
                                            <tr>
                                                <Th className="w-14">
                                                    <label className="inline-flex p-2 -m-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            aria-label="Select every account listed"
                                                            checked={checked.size === candidates.length}
                                                            onChange={e => setChecked(e.target.checked ? new Set(candidates.map(c => c.customerId)) : new Set())}
                                                        />
                                                    </label>
                                                </Th>
                                                <Th>Account</Th>
                                                <Th className="w-32">Code</Th>
                                                <Th>Current owner</Th>
                                                <Th>Proposed owner</Th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {candidates.map(c => (
                                                <tr key={c.customerId} className="hover:bg-hover">
                                                    <Td>
                                                        <label className="inline-flex p-2 -m-2 cursor-pointer">
                                                            <input
                                                                type="checkbox" checked={checked.has(c.customerId)}
                                                                aria-label={`Select ${c.company}`}
                                                                onChange={e => setChecked(prev => {
                                                                    const next = new Set(prev);
                                                                    if (e.target.checked) next.add(c.customerId); else next.delete(c.customerId);
                                                                    return next;
                                                                })}
                                                            />
                                                        </label>
                                                    </Td>
                                                    <Td className="text-label">{c.company}</Td>
                                                    <Td className="num">{c.importedCode}</Td>
                                                    <Td className="text-label-3">{c.currentOwnerName ?? 'Unassigned'}</Td>
                                                    <Td className="font-semibold text-label">{c.proposedOwnerName}</Td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <div className="px-5 py-3 border-t border-separator">
                                        <Button variant="primary" disabled={applying || checked.size === 0} onClick={() => { void runApply(); }} busy={applying}>
                                            {applying ? 'Applying…' : `Apply to ${checked.size} account${checked.size === 1 ? '' : 's'}`}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Card>
                    )}
                </>
            )}
        </Page>
    );
}
